/**
 * Talking to the kernel: auth, then reads.
 *
 * The auth half is the shape LO-15 proved in a browser — authorization code
 * with PKCE, access token in sessionStorage. Nothing here is new; it is here
 * because a second app hand-rolling the same flow slightly differently is how
 * two apps end up with two bugs.
 *
 * This is the **host** half — an app signing a *person* in, against its own
 * Cognito app client. A micro-app mounted inside such a host does not use it:
 * it signs in as *itself*, which is `@lopie/kernel-ui/remote-auth`. Borrowing
 * a host's token means inheriting the host's manifest, and the console's is
 * the widest in the system.
 */

// ---------------------------------------------------------------------------
// Where the environment facts come from
//
// `window.KERNEL_CONFIG`, written by a `config.js` outside the bundle, so one
// build artifact moves between environments by swapping a file. Read through
// a function rather than captured at module scope: a package is imported
// before an app's own code runs, and a top-level `window.KERNEL_CONFIG` read
// binds `undefined` forever if the script tag happens to load second. That is
// a failure whose only symptom is `Cannot read properties of undefined`
// pointing at a line in a dependency.
//
// `configure()` exists for an app that would rather pass the facts in — a
// test, or a host that already knows them — and is not the normal path.
// ---------------------------------------------------------------------------

let injected = null;

/** Supply config explicitly instead of reading `window.KERNEL_CONFIG`. */
export function configure(config) {
  injected = config;
}

/** The resolved config, for the host half of the remote contract. Exported
 *  so `host.js` reads the same object rather than keeping a second reader. */
export function hostConfig() {
  return conf();
}

function conf() {
  const c = injected || (typeof window !== "undefined" ? window.KERNEL_CONFIG : null);
  if (!c) {
    throw new Error(
      "no kernel config: load a config.js that sets window.KERNEL_CONFIG, or " +
        "call configure() before using the client",
    );
  }
  return c;
}

// ---------------------------------------------------------------------------
// PKCE
//
// The authorization code comes back through the address bar, where anything on
// the machine can read it. The verifier proves we are the client that started
// the flow, so an intercepted code is not enough to get a token.
// ---------------------------------------------------------------------------

const b64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return b64url(digest);
}

export async function signIn() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  sessionStorage.setItem("kernel.pkce", verifier);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: conf().clientId,
    redirect_uri: conf().redirect,
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: await challengeFor(verifier),
  });
  window.location = `${conf().login}/oauth2/authorize?${params}`;
}

export function signOut() {
  sessionStorage.removeItem("kernel.token");
  const params = new URLSearchParams({
    client_id: conf().clientId,
    // Comes back with a marker so the app knows this was deliberate. Without
    // it the auto-redirect sends you straight back to the login page and the
    // sign-out button appears to do nothing.
    logout_uri: `${conf().redirect}?signedout=1`,
  });
  window.location = `${conf().login}/logout?${params}`;
}

async function exchange(code) {
  const verifier = sessionStorage.getItem("kernel.pkce") || "";
  sessionStorage.removeItem("kernel.pkce");

  const response = await fetch(`${conf().login}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: conf().clientId,
      code,
      redirect_uri: conf().redirect,
      code_verifier: verifier,
    }),
  });
  if (!response.ok) {
    throw new Error(`sign-in failed: ${response.status} ${await response.text()}`);
  }
  const body = await response.json();
  // The access token, not the id token. API Gateway matches its audience list
  // against `client_id`, which only the access token carries, and
  // `cognito:groups` rides on it too.
  sessionStorage.setItem("kernel.token", body.access_token);
  return body.access_token;
}

export function claimsOf(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
  } catch {
    return {};
  }
}

/** Resolve the session on load: finish a redirect, or restore a live token. */
export async function resume() {
  const url = new URL(window.location);
  const code = url.searchParams.get("code");

  if (code) {
    const token = await exchange(code);
    // A spent code left in the address bar means a refresh tries to spend it
    // again and fails in a way that looks like the app is broken.
    window.history.replaceState({}, "", url.pathname);
    return { token, claims: claimsOf(token) };
  }

  const token = sessionStorage.getItem("kernel.token");
  if (!token) return null;

  const claims = claimsOf(token);
  if (claims.exp && claims.exp * 1000 < Date.now()) {
    sessionStorage.removeItem("kernel.token");
    return null;
  }
  return { token, claims };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * A refusal is information, so it survives as structure rather than a string.
 *
 * `not_in_manifest` naming a type and tag means the app was never allowed to
 * ask. An empty page means the user cannot see anything. Those are different
 * sentences and a UI that renders both as "no results" has thrown away the
 * only part a reader could act on.
 */
export class KernelError extends Error {
  constructor(status, body, fallback) {
    super(body?.detail || body?.error || fallback);
    this.status = status;
    this.body = body || {};
    this.error = body?.error;
    this.parameter = body?.parameter;
  }

  /** True when the *app's* manifest is what refused this, not the user's
   *  permissions. The two want different sentences on screen. */
  get isManifest() {
    return this.error === "not_in_manifest" || this.error === "no_manifest";
  }
}

/**
 * The control plane is a second API, and it is not the kernel.
 *
 * `control.lopie.one` answers what Lopie publishes and what this org has
 * installed — a catalog and a set of deployments. The kernel answers what an
 * app may touch and what is in the graph. Two questions, two services, and
 * one of them can create infrastructure in another AWS account.
 *
 * The **token is the same one**, which is worth stating because it looks like
 * it should not be: same pool, same sign-in, and the control plane's
 * authorizer lists exactly one audience — the console's client. So a micro-app
 * token gets a 401 there even though it works perfectly against the kernel,
 * which is the intended shape rather than a gap.
 *
 * Separate functions rather than a `base` argument on `kernel()` because the
 * two are not interchangeable. A screen reaching for the wrong one should
 * fail at the import, not at runtime against an endpoint that happens to 404.
 */
function controlBase() {
  const base = conf().control;
  if (!base) {
    throw new Error(
      "no control-plane URL in the kernel config: this build cannot reach " +
        "control.lopie.one, so the app library is unavailable",
    );
  }
  return base;
}

export function controlGet(token) {
  return async function get(path) {
    const response = await fetch(controlBase() + path, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function controlPost(token) {
  return async function post(path, body) {
    const response = await fetch(controlBase() + path, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify(body || {}),
    });
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      // The control plane's errors name a `field`, which the kernel's do not.
      // Carried through as-is so a form can put the message under the input
      // that caused it rather than at the top of the page.
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function kernelPost(token) {
  return async function post(path, body) {
    const response = await fetch(conf().api + path, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify(body || {}),
    });
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function kernel(token) {
  return async function get(path, params) {
    const url = new URL(conf().api + path);
    for (const [key, value] of Object.entries(params || {})) {
      if (value !== "" && value != null) url.searchParams.set(key, value);
    }

    let response;
    try {
      response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        // The API sends `cache-control: no-store` and this says the same
        // thing from the other end. Belt and braces on purpose: a screen
        // about who can see what, served from a cache, is the one kind of
        // stale this product cannot afford.
        cache: "no-store",
      });
    } catch (cause) {
      // Browsers say "Failed to fetch" for CORS, DNS and offline alike. It is
      // worth saying which one is likely rather than passing that on.
      throw new KernelError(0, null, `cannot reach ${conf().api} — network or CORS`);
    }

    const text = await response.text();
    let body;
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }

    if (!response.ok) {
      throw new KernelError(response.status, body, text || response.statusText);
    }
    // A 200 that is not JSON is a failure, and it used to be returned as
    // `null`. Every caller then read a field off it and the console
    // white-screened with a `TypeError` and no sentence — which is the worst
    // possible version of a refusal, since a blank page tells the reader
    // nothing at all about whose decision produced it.
    //
    // It is not hypothetical: it happened while driving the console in a
    // browser, when an unrelated dev server on the same port answered
    // `/schema` with an HTML page and the whole app went blank. A proxy, a
    // captive portal or a CloudFront error page all do the same thing, and
    // none of them is a 4xx.
    if (body === null && text.trim() !== "") {
      throw new KernelError(response.status, null, (
        `${conf().api}${path} answered ${response.status} with something that ` +
        "is not JSON. That is not a permission decision — something between " +
        "this page and the kernel answered instead of the kernel."
      ));
    }
    return body;
  };
}
